import { useEffect, useMemo, useRef, useState } from "react";

import { useLang } from "../services/lang";
import { Input } from "./Form";
import Dialog from "./Dialog";
import { ConfirmButton, ContentSwitch, Loading } from ".";
import { handleError, setAuthorizationHeader } from "../services/api";

import { User } from "../../types";
import Icon from "@mdi/react";
import { mdiBriefcase, mdiLogin, mdiLogout, mdiRun } from "@mdi/js";

const colors = ["green", "yellow", "orange", "brawn", "red", "blue", "purple", "pink", "light", "dark"];

function getRandomColor() {
    return colors[Math.floor(Math.random() * colors.length)];
}

const icons = ["👨", "👩", "🙋", "🤔", "😎", "🥳", "🎅", "👹", "👤", "🖖", "🙊", "🐢", "🐷", "🐻", "🦄", "🦥", "🦔", "🐌"];

function getRandomIcon() {
    return icons[Math.floor(Math.random() * icons.length)];
}

function getIconFromName(name: string) {
    if (!name) return getRandomIcon();

    const [first, second] = name.trim().split(" ").map(n => n.trim());

    if (second) {
        return first[0].toUpperCase() + second[0].toUpperCase();
    } else {
        return first.slice(0, 2).toUpperCase();
    }
}

import { UserProvider as EasyLoginProvider, useUser as useEasyLoginUser, LogInForm, RegisterForm, ForgottenPasswordForm } from "easy-user-auth/react";

export function useUser() {
    return useEasyLoginUser<User>();
}

function AuthHeaderSync() {
    const { isLoggedIn } = useUser();

    useEffect(() => {
        if (!isLoggedIn) {
            setAuthorizationHeader("");
        } else {
            try {
                const secretsStr = localStorage.getItem("user-secrets");
                if (secretsStr) {
                    const secrets = JSON.parse(secretsStr);
                    if (secrets && secrets.token) {
                        setAuthorizationHeader("bearer " + secrets.token);
                    }
                }
            } catch (_) { }
        }
    }, [isLoggedIn]);

    return null;
}

export function UserProvider({ children }: { children: React.ReactNode }) {
    const { dict, lang } = useLang();
    const newUser: User = { name: "", color: getRandomColor(), icon: getRandomIcon() };

    return (
        <EasyLoginProvider<User>
            defaultUser={newUser}
            dict={dict}
            serverUrl={process.env.NODE_ENV === "development" ? "http://localhost:1111" : ""}
            renderDialog={(show, onClose, dialogChildren) => {
                const isForgot = dialogChildren && (dialogChildren as any).type === ForgottenPasswordForm;
                return (
                    <Dialog show={show} onClose={onClose}>
                        {isForgot ? dialogChildren : <UserDialog />}
                    </Dialog>
                );
            }}
        >
            <AuthHeaderSync />
            {children}
        </EasyLoginProvider>
    );
}

function UserDialog() {
    const { dict } = useLang();
    const { isLoggedIn, loginUser } = useUser();

    const [contentIndex, setContentIndex] = useState(0);

    return <div className="user-dialog">
        {isLoggedIn && <>
            <p>{dict.userDialogLoggedInHeader}</p>
            <UserInput />
            <button onClick={() => loginUser(null)}>{dict.logOut}</button>
        </>}

        {!isLoggedIn && <ContentSwitch
            index={contentIndex}
            onChange={setContentIndex}
            options={[{
                title: dict.logIn,
                content: <LogInForm />,
            }, {
                title: dict.userCreateAccount,
                content: <RegisterForm />,
            }]}
        />}
    </div>;
}

export function UserInput({ hideLogInOut = false }: { hideLogInOut?: boolean }) {
    const { dict } = useLang();

    const { user, setUser, isLoggedIn, showUserDialog, loginUser } = useUser();

    const [isEditing, setIsEditing] = useState(false);

    const [customIcon, setCustomIcon] = useState("");

    const refEdit = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!isEditing) return;

        function handleClickOutside(event: MouseEvent) {
            if (refEdit.current && !refEdit.current.contains(event.target as Node)) {
                setIsEditing(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        }
    }, [isEditing]);

    const monograms = useMemo(() => {
        if (!user.name) return ["X"]

        const monograms: string[] = [];

        if (user.name.length > 0) {
            const [first, second] = user.name.trim().split(" ").map(n => n.trim());

            if (first)
                monograms.push(first[0].toUpperCase());
            if (first.length > 1)
                monograms.push(first.slice(0, 2).toUpperCase());
            if (second)
                monograms.push(first[0].toUpperCase() + second[0].toUpperCase());
        }

        return monograms;
    }, [user.name]);

    function handleCustomIcon(text: string) {
        const icon = text.slice(0, 2);
        setCustomIcon(icon);
        setUser({ ...user, icon });
    }

    return <div className="user-input">
        <div className={`avatar block bg-${user.color}`} onClick={() => setIsEditing(true)}>
            {user.icon}
            {<div className={`edit-button block ${isEditing ? "" : "show"}`}>{dict.change}</div>}
        </div>
        <Input className="name-input" type="text" maxLength={50} placeholder={dict.userNamePlaceholder} value={user} field="name" setValue={setUser} />
        {hideLogInOut !== true && <>
            {isLoggedIn && <ConfirmButton tag="a" className="user-logout" text="Oprávdu se cheš odhlásit z tohoto zařízení?" onClick={() => loginUser(null)}><Icon path={mdiLogout} size={1} /></ConfirmButton>}
            {!isLoggedIn && <a className="user-login" onClick={showUserDialog}><Icon path={mdiLogin} size={1} /></a>}
        </>}

        <div ref={refEdit} className={`edit block ${isEditing ? "show" : ""}`}>
            <h4>{dict.userMonogram}</h4>
            <div className="monograms">
                {monograms.map(m => <div key={m} className="block monogram" onClick={() => setUser({ ...user, icon: m })}>{m}</div>)}
            </div>
            <h4>{dict.userEmoji}</h4>
            <div className="monograms">
                {icons.map(m => <div key={m} className="block monogram" onClick={() => setUser({ ...user, icon: m })}>{m}</div>)}
            </div>
            <input type="text" value={customIcon} placeholder={dict.userMonogramPlaceholder} onChange={e => handleCustomIcon(e.target.value)} />

            <h4>{dict.userBackground}</h4>
            <div className="colors">
                {colors.map(c => <div key={c} className={`block color bg-${c}`} onClick={() => setUser({ ...user, color: c })} />)}
            </div>
            <button className="small" onClick={() => setIsEditing(false)}>{dict.save}</button>
        </div>
    </div>;
}

export function User({ name, color, icon, showFull, isAdmin, isMember }: User & { showFull?: boolean, isAdmin?: boolean, isMember?: boolean }) {
    const { dict } = useLang();

    return <div className="user">
        <div className={`avatar block bg-${color}`}>
            {icon}
            {!showFull && isAdmin === true && <div className="admin"><Icon path={mdiBriefcase} size={0.8} /></div>}
            {!showFull && isMember === false && <div className="admin"><Icon path={mdiRun} size={0.8} /></div>}
        </div>
        <div className="name">
            {name}
            {showFull && isAdmin === true && <div className="admin"><Icon path={mdiBriefcase} size={0.8} /> {dict.userAdmin}</div>}
            {showFull && isMember === false && <div className="admin"><Icon path={mdiRun} size={0.8} /> {dict.userNotMember}</div>}
        </div>
    </div>;
}

export function UserVote({ user, comment }: { user?: User, comment?: string }) {
    const { dict } = useLang();

    return <>
        {user && <User {...user} />}
        {!user && <User key={user} name={dict.userNameUnknown} color="silver" icon="?" />}
        {comment && <div className="user-comment">{comment}</div>}
    </>;
}